// databaseTypeLabels.js — validation and formatting for base database type names

export const MAX_DATABASE_TYPE_LABEL_LENGTH = 24;
export const MAX_DATABASE_TYPE_LABEL_WORDS = 2;
const SHORT_ACRONYM_MAX_LENGTH = 4;

const ALLOWED_LABEL_PATTERN = /^[a-zA-Z0-9\s&-]+$/;

export const FORMAT_ERROR = 'Keep database names short — use one or two words (for example, Contact or Work Order).';
export const LENGTH_ERROR = `Database names must be ${MAX_DATABASE_TYPE_LABEL_LENGTH} characters or fewer.`;
export const CHARS_ERROR = 'Database names can only contain letters, numbers, spaces, hyphens, and ampersands.';

function cleanDatabaseTypeLabelInput(value) {
  return String(value || '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/[?.!]+$/, '')
    .trim();
}

function titleCaseDatabaseTypeWord(word) {
  const trimmed = String(word || '').trim();
  if (!trimmed) return '';
  if (trimmed === '&') return '&';

  if (trimmed.includes('-')) {
    return trimmed
      .split('-')
      .filter(Boolean)
      .map((segment) => titleCaseDatabaseTypeWord(segment))
      .join('-');
  }

  if (trimmed.includes('&')) {
    return trimmed
      .split('&')
      .map((segment) => titleCaseDatabaseTypeWord(segment))
      .join('&');
  }

  const lettersOnly = trimmed.replace(/[^a-zA-Z]/g, '');
  const isLettersOnly = lettersOnly.length === trimmed.length;
  const acronymLength = lettersOnly.length;

  if (
    isLettersOnly
    && acronymLength >= 3
    && acronymLength <= SHORT_ACRONYM_MAX_LENGTH
    && trimmed === trimmed.toUpperCase()
  ) {
    return trimmed;
  }

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function countDatabaseTypeLabelWords(value) {
  return cleanDatabaseTypeLabelInput(value)
    .split(/\s+/)
    .filter((token) => token && token !== '&')
    .length;
}

export function formatDatabaseTypeLabel(value) {
  const cleaned = cleanDatabaseTypeLabelInput(value);
  if (!cleaned) return '';

  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => titleCaseDatabaseTypeWord(word))
    .join(' ');
}

export function getDatabaseTypeLabelFormatError(value) {
  const cleaned = cleanDatabaseTypeLabelInput(value);
  if (!cleaned) return '';

  if (!ALLOWED_LABEL_PATTERN.test(cleaned)) {
    return CHARS_ERROR;
  }

  if (countDatabaseTypeLabelWords(cleaned) > MAX_DATABASE_TYPE_LABEL_WORDS) {
    return FORMAT_ERROR;
  }

  if (cleaned.length > MAX_DATABASE_TYPE_LABEL_LENGTH) {
    return LENGTH_ERROR;
  }

  return '';
}

export function resolveDatabaseTypeLabel(value) {
  const formatted = formatDatabaseTypeLabel(value);
  return {
    formatted,
    error: getDatabaseTypeLabelFormatError(formatted),
  };
}
